import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import debounce from 'lodash/debounce';

import { fetchAdditionalItems } from 'Shared/actions/todos';
import { getTimelineGroups } from 'Shared/actions/workspaces';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import Spinner from 'Shared/components/Spinner';
import { SelectorValue } from 'Shared/constants/types';
import { WORKSPACE_CONTENT_CONTAINER_ID } from 'Shared/constants/constants';

import TimelineGroupItem from './TimelineGroupItem/TimelineGroupItem';
import WorkspaceTimeLine from './WorkspaceTimeLine/WorkspaceTimeLine';
import { getTimelineLabel } from './utils';

type Props = {
  groupTimelineFilterValue: SelectorValue;
};

const TimelineViewSection = ({ groupTimelineFilterValue }: Props) => {
  const dispatch = useDispatch();
  const workspaceId = useSelector(state => state.workspace.id);
  const timelineGroups = useDeepEqualSelector(state =>
    state.workspace?.requestedTimelineFilters[groupTimelineFilterValue.value]
      ?.map(timelineGroup => state.workspace?.timelineGroups[timelineGroup])
      .filter(value => value)
  );
  const allTimelineGroupsCount = useSelector(state => state.workspace?.timelineGroups.length);
  const [countCompletedItems, setCountCompletedItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(timelineGroups.length);
  const itemsRefs = useRef([]);

  useEffect(() => {
    async function fetchTimelineGroups() {
      setIsLoading(true);
      await dispatch(getTimelineGroups({ workspaceId, groupBy: groupTimelineFilterValue.value }));
      setIsLoading(false);
    }
    fetchTimelineGroups();
  }, []);

  const handleWorkspaceTimeLineDateClick = weekAgo => {
    itemsRefs.current[weekAgo].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
  };

  const fetchAdditionalCompletedItems = useCallback(async () => {
    const nextCurrentOffset = currentOffset + 4;
    const count = await dispatch(
      fetchAdditionalItems({
        workspaceId,
        filter: 'timeline',
        minWeeksAgo: currentOffset,
        maxWeeksAgo: nextCurrentOffset,
        groupBy: groupTimelineFilterValue.value,
      })
    );
    if (countCompletedItems !== count) setCountCompletedItems(count || 0);
    setCurrentOffset(nextCurrentOffset);
  }, [dispatch, workspaceId, currentOffset, groupTimelineFilterValue.value, countCompletedItems]);

  const debounceFetchAdditionalItems = useCallback(debounce(fetchAdditionalCompletedItems, 1000), [
    fetchAdditionalCompletedItems,
  ]);

  return isLoading ? (
    <Spinner activeIf />
  ) : (
    <>
      <WorkspaceTimeLine onItemClick={handleWorkspaceTimeLineDateClick} timelineGroups={timelineGroups} />
      <InfiniteScroll
        pageStart={0}
        loadMore={debounceFetchAdditionalItems}
        hasMore={allTimelineGroupsCount > timelineGroups.length}
        threshold={10}
        loader={<Spinner key="scroll-spinner" activeIf className="mt-5" />}
        useWindow={false}
        getScrollParent={() => document.getElementById(WORKSPACE_CONTENT_CONTAINER_ID)}
      >
        {timelineGroups.map(({ weeksAgo }) => (
          <div
            key={`timeline-group-item-${weeksAgo}`}
            ref={el => {
              itemsRefs.current[weeksAgo] = el;
            }}
          >
            <TimelineGroupItem
              label={getTimelineLabel(weeksAgo)}
              weeksAgo={weeksAgo}
              groupTimelineFilterValue={groupTimelineFilterValue}
            />
          </div>
        ))}
      </InfiniteScroll>
    </>
  );
};

export default TimelineViewSection;
